import {
    ClaimTimelineCardStyles as Card,
    DocumentViewer,
    ProviderDocumentUrl,
    ProviderInstitutionalClaim,
    ProviderInstitutionalClaimTemplate,
    ProviderNynf3Claim,
    ProviderNynf3ClaimTemplate,
    ProviderPayerSubscriber,
    ProviderProfessionalClaim,
    ProviderProfessionalClaimTemplate,
    ProviderSubscriberPatient,
    Spinner,
    formatClaimTemplateServiceLines,
} from '@enter/core';
import _ from 'lodash';
import { inject, observer } from 'mobx-react';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { DialogContext } from 'services';
import { RootStore } from 'stores';
import { ClaimService } from 'stores/claim.service';

interface IClaimDetailProps {
    claimId?: string;
    performedBy?: string;
    claimPind: ProviderProfessionalClaim | ProviderInstitutionalClaim | ProviderNynf3Claim;
    primaryPayerSubscriber: ProviderPayerSubscriber | undefined;
    primarySubscriberPatient: ProviderSubscriberPatient | undefined;
    template?: ProviderProfessionalClaimTemplate | ProviderInstitutionalClaimTemplate | ProviderNynf3ClaimTemplate;
}

export const ClaimDetail: React.FunctionComponent<IClaimDetailProps> = inject('rootStore')(
    observer(
        (props: IClaimDetailProps): React.ReactElement => {
            const rootStore: RootStore = _.get(props, 'rootStore');

            if (!rootStore) {
                return <></>;
            }

            const dialogContext = useContext(DialogContext);
            const { claimId, performedBy, claimPind, primaryPayerSubscriber, primarySubscriberPatient, template } =
                props;
            const [loading, setLoading] = useState(false);
            const [documentUrl, setDocumentUrl] = useState<ProviderDocumentUrl | undefined>();
            const isInstitutional = !!(claimPind as ProviderInstitutionalClaim).institutionalTemplateId;
            const serviceLines = useMemo(() => formatClaimTemplateServiceLines(template), [template]);

            useEffect(() => {
                (async (): Promise<void> => {
                    if (!claimPind?.id) {
                        return;
                    }

                    if (
                        !isInstitutional &&
                        (!primaryPayerSubscriber?.id || !primarySubscriberPatient?.id)
                    ) {
                        dialogContext.showError(new Error('Can`t fetch the document'));
                        return;
                    }

                    try {
                        setLoading(true);

                        if (isInstitutional) {
                            const institutionalDocumentUrl = await ClaimService.viewCMS1450({
                                id: claimPind.id,
                            });

                            setDocumentUrl(institutionalDocumentUrl);
                        } else {
                            const professionalDocumentUrl = await ClaimService.viewCMS1500({
                                id: claimPind.id,
                                payerSubscriberId: primaryPayerSubscriber?.id,
                                subscriberPatientId: primarySubscriberPatient?.id,
                            });

                            setDocumentUrl(professionalDocumentUrl);
                        }
                    } catch (err) {
                        dialogContext.showError(err);
                    } finally {
                        setLoading(false);
                    }
                })();
            }, [
                claimPind?.id,
                dialogContext,
                isInstitutional,
                primaryPayerSubscriber?.id,
                primarySubscriberPatient?.id,
            ]);

            return (
                <Card.CdPanel $embedded>
                    <Card.CdSectionCard $embedded>
                        <Card.CdFactsGrid>
                            {claimId && (
                                <Card.CdFactCell>
                                    <Card.CdFactLabel>Claim ID</Card.CdFactLabel>
                                    <Card.CdFactValue $mono>{claimId}</Card.CdFactValue>
                                </Card.CdFactCell>
                            )}
                            <Card.CdFactCell $span={2}>
                                <Card.CdFactLabel>Service lines</Card.CdFactLabel>
                                <Card.CdFactValue>{serviceLines || '—'}</Card.CdFactValue>
                            </Card.CdFactCell>
                            {performedBy && (
                                <Card.CdFactCell>
                                    <Card.CdFactLabel>Performed by</Card.CdFactLabel>
                                    <Card.CdFactValue>{performedBy}</Card.CdFactValue>
                                </Card.CdFactCell>
                            )}
                        </Card.CdFactsGrid>
                    </Card.CdSectionCard>

                    <Card.CdSectionCard $embedded>
                        {loading ? <Spinner /> : documentUrl?.url && <DocumentViewer url={documentUrl.url} />}
                    </Card.CdSectionCard>
                </Card.CdPanel>
            );
        },
    ),
);
